import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Send, Lock, User2, Settings2, Clock, UserCheck,
} from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi , useSuperAdminAuth } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { formatDateTime } from '@/lib/dateFormat';
import {
  STATUS_COLORS,
  PRIORITY_COLORS,
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  CATEGORIES,
  SCOPE_LABELS,
  humanStatus,
  type Ticket,
  type ThreadMessage,
  type TicketStatus,
  type TicketPriority,
} from '../support/supportCommon';
import { SelectField } from '@/components/ui/SelectField';

interface AdminUser { id: string; name: string; email?: string; role?: string; }

export function SuperAdminTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <SuperAdminLayout title="Ticket">
      {id && <SuperAdminTicketView ticketId={id} />}
    </SuperAdminLayout>
  );
}

/**
 * Ticket conversation + sidebar. Rendered by the full page and by the
 * Support Inbox side drawer (`embedded`): embedded hides "Back to Inbox",
 * closes via onClose instead of navigating, and calls onChanged after any
 * reply / status / assignment change so the list refreshes.
 */
export function SuperAdminTicketView({ ticketId, embedded = false, onClose, onChanged }: {
  ticketId: string;
  embedded?: boolean;
  onClose?: () => void;
  onChanged?: () => void;
}) {
  const id = ticketId;
  const navigate = useNavigate();
  const { admin } = useSuperAdminAuth();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);

  const [reply, setReply] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [posting, setPosting] = useState(false);

  const [savingMeta, setSavingMeta] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [t, m] = await Promise.all([
        saApi.get(`/support/admin/tickets/${id}`),
        saApi.get(`/support/admin/tickets/${id}/messages`),
      ]);
      setTicket(t.data);
      setMessages(Array.isArray(m.data) ? m.data : []);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load ticket');
      if (onClose) onClose(); else navigate('/super-admin/tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // Fetch super-admin users for assignment dropdown.
  useEffect(() => {
    saApi.get('/super-admin/users')
      .then((res) => {
        const list: AdminUser[] = (res.data?.data ?? res.data ?? [])
          .map((u: any) => ({ id: u.id, name: u.name, email: u.email, role: u.role }));
        setAdmins(list);
      })
      .catch(() => {});
  }, []);

  const postMessage = async () => {
    if (!reply.trim() || !ticket) return;
    setPosting(true);
    try {
      await saApi.post(`/support/admin/tickets/${ticket.id}/messages`, {
        body: reply.trim(),
        isInternal,
      });
      setReply('');
      const wasInternal = isInternal;
      setIsInternal(false);
      toast('success', wasInternal ? 'Internal note added' : 'Reply sent — tenant will be emailed');
      await load();
      onChanged?.();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const updateMeta = async (patch: { status?: TicketStatus; priority?: TicketPriority }) => {
    if (!ticket) return;
    setSavingMeta(true);
    try {
      await saApi.patch(`/support/admin/tickets/${ticket.id}`, patch);
      await load();
      onChanged?.();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to update');
    } finally {
      setSavingMeta(false);
    }
  };


  if (loading || !ticket) {
    return (
      <div className="p-10 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" />
      </div>
    );
  }

  const myId = admin?.id ?? null;
  const isAssignedToMe = ticket.assignedAdminId && ticket.assignedAdminId === myId;

  return (
      <div className={embedded ? '' : 'p-6 max-w-7xl mx-auto'}>
        {/* Back — preserve which tab the ticket belongs to (full page only) */}
        {!embedded && <button
          onClick={() => {
            const tab = ticket.scope === 'platform_internal' ? 'platform_internal' : 'platform';
            navigate(`/super-admin/tickets?tab=${tab}`);
          }}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Inbox
        </button>}

        {/* Layout: main + sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
          {/* Main */}
          <div>
            {/* Header */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl font-semibold text-slate-900 break-words">{ticket.subject}</h1>
                  <p className="text-xs text-slate-500 mt-1">
                    <span className="font-mono">#{ticket.id.slice(-6).toUpperCase()}</span>
                    {' · From '}
                    {ticket.userName ?? '—'} &lt;{ticket.userEmail}&gt;
                    {' · '}
                    Submitted {formatDateTime(ticket.createdAt)}
                  </p>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full border ${(SCOPE_LABELS[ticket.scope] ?? SCOPE_LABELS.platform).color} flex-shrink-0`}>
                  {(SCOPE_LABELS[ticket.scope] ?? SCOPE_LABELS.platform).short}
                </span>
              </div>
            </div>

            {/* Conversation */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-50">
                <span className="text-sm font-medium text-slate-700">Conversation</span>
              </div>
              <div className="px-5 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
                {/* Original tenant message — always first */}
                <Message
                  author={ticket.userName ?? 'Tenant'}
                  type="tenant"
                  body={ticket.message}
                  at={ticket.createdAt}
                  isInternal={false}
                />
                {messages.map((m) => (
                  <Message
                    key={m.id}
                    author={m.authorName ?? (m.authorType === 'admin' ? 'You' : 'Tenant')}
                    type={m.authorType}
                    body={m.body}
                    at={m.createdAt}
                    isInternal={m.isInternal}
                  />
                ))}
              </div>

              {/* Reply composer */}
              <div className={`px-5 py-4 border-t border-gray-50 ${isInternal ? 'bg-amber-50/50' : 'bg-slate-50/40'}`}>
                <textarea
                  rows={4}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={isInternal
                    ? 'Internal note — visible to your team only.'
                    : 'Reply to the tenant. They will receive an email.'}
                  className={`w-full px-3 py-2 rounded-lg border bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 resize-none placeholder:text-slate-400 ${
                    isInternal
                      ? 'border-amber-200 focus:ring-amber-400'
                      : 'border-gray-200 focus:ring-[#6366F1]'
                  }`}
                  maxLength={10000}
                />
                <div className="flex items-center justify-between mt-2">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-normal text-amber-700">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      className="h-3.5 w-3.5 accent-amber-500"
                    />
                    <Lock className="h-3 w-3" />
                    Internal note (hidden from tenant)
                  </label>
                  <button
                    onClick={postMessage}
                    disabled={posting || !reply.trim()}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60 ${
                      isInternal ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#6366F1] hover:bg-[#4F46E5]'
                    }`}>
                    {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : isInternal ? <Lock className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
                    {isInternal ? 'Save Note' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <SidebarCard title="Properties">
              <SidebarRow label="Status">
                <SelectField
                  value={ticket.status}
                  onChange={(e) => updateMeta({ status: e.target.value as TicketStatus })}
                  disabled={savingMeta}
                  className="w-full h-9 px-2.5 rounded-lg border border-gray-200 text-xs bg-white">
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{humanStatus(s)}</option>)}
                </SelectField>
                <span className={`mt-1 inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${STATUS_COLORS[ticket.status]}`}>
                  {humanStatus(ticket.status)}
                </span>
              </SidebarRow>
              <SidebarRow label="Priority">
                <SelectField
                  value={ticket.priority}
                  onChange={(e) => updateMeta({ priority: e.target.value as TicketPriority })}
                  disabled={savingMeta}
                  className="w-full h-9 px-2.5 rounded-lg border border-gray-200 text-xs bg-white">
                  {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </SelectField>
                <span className={`mt-1 inline-block text-[10px] font-medium uppercase px-2 py-0.5 rounded-full ${PRIORITY_COLORS[ticket.priority]}`}>
                  {ticket.priority}
                </span>
              </SidebarRow>
              <SidebarRow label="Category">
                <span className="text-sm text-slate-700">
                  {CATEGORIES.find((c) => c.v === ticket.category)?.l ?? ticket.category}
                </span>
              </SidebarRow>
            </SidebarCard>

            <SidebarCard title="Assigned Agent">
              <SelectField
                value={ticket.assignedAdminId ?? ''}
                onChange={(e) => {
                  const uid = e.target.value || null;
                  const uname = uid ? admins.find((a) => a.id === uid)?.name ?? null : null;
                  saApi.patch(`/support/admin/tickets/${ticket.id}`, {
                    assignedAdminId: uid,
                    assignedAdminName: uname,
                  })
                    .then(() => { load(); onChanged?.(); })
                    .catch((err: any) => toast('error', err?.response?.data?.message || 'Failed to assign'));
                }}
                disabled={savingMeta}
                className="w-full h-9 px-2.5 rounded-lg border border-gray-200 text-xs bg-white">
                <option value="">— Unassigned —</option>
                {myId && (
                  <option value={myId}>👤 Assign to me ({admin?.name})</option>
                )}
                {admins.filter((a) => a.id !== myId).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.role ? ` (${a.role})` : ''}
                  </option>
                ))}
              </SelectField>
              {ticket.assignedAdminName && (
                <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                  <UserCheck className="h-3 w-3" />
                  {ticket.assignedAdminName}
                  {isAssignedToMe && <span className="font-bold ml-1">(YOU)</span>}
                </p>
              )}
            </SidebarCard>

            <SidebarCard title="Requester">
              <p className="text-sm font-medium text-slate-800">{ticket.userName ?? '—'}</p>
              <p className="text-xs text-slate-500 mt-0.5">{ticket.userEmail}</p>
              <p className="text-xs text-slate-400 mt-2 font-mono">Tenant: {ticket.tenantId.slice(-12)}</p>
            </SidebarCard>

            <SidebarCard title="Timeline">
              <SidebarRow label="Created" small>{formatDateTime(ticket.createdAt)}</SidebarRow>
              <SidebarRow label="Updated" small>{formatDateTime(ticket.updatedAt)}</SidebarRow>
              {ticket.repliedAt && <SidebarRow label="Last Reply" small>{formatDateTime(ticket.repliedAt)}</SidebarRow>}
              {ticket.closedAt && <SidebarRow label="Closed" small>{formatDateTime(ticket.closedAt)}</SidebarRow>}
            </SidebarCard>
          </div>
        </div>
      </div>
  );
}

// ── Message bubble (agent view) ──────────────────────────────────────────────
function Message({
  author,
  type,
  body,
  at,
  isInternal,
}: {
  author: string;
  type: 'tenant' | 'admin' | 'system';
  body: string;
  at: string;
  isInternal: boolean;
}) {
  if (type === 'system') {
    return (
      <div className="flex items-center justify-center gap-2 py-2 text-xs text-slate-400 italic">
        <Settings2 className="h-3 w-3" />
        <span>{body}</span>
        <span>·</span>
        <span><Clock className="inline h-3 w-3 mr-0.5" />{formatDateTime(at)}</span>
      </div>
    );
  }

  const isTenant = type === 'tenant';
  return (
    <div className={`flex gap-3 ${isTenant ? 'justify-start' : 'justify-end'}`}>
      {isTenant && (
        <div className="h-8 w-8 rounded-full bg-slate-200 flex-shrink-0 flex items-center justify-center">
          <User2 className="h-4 w-4 text-slate-600" />
        </div>
      )}
      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 border ${
        isInternal
          ? 'bg-amber-50 border-amber-200 rounded-tr-sm'
          : isTenant
            ? 'bg-slate-50 border-slate-200 rounded-tl-sm'
            : 'bg-indigo-50 border-indigo-100 rounded-tr-sm'
      }`}>
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <span className={`text-[11px] font-semibold flex items-center gap-1 ${
            isInternal ? 'text-amber-700' : isTenant ? 'text-slate-700' : 'text-indigo-700'
          }`}>
            {isInternal && <Lock className="h-2.5 w-2.5" />}
            {isInternal ? `${author} (internal)` : author}
          </span>
          <span className="text-[10px] text-slate-400 whitespace-nowrap">{formatDateTime(at)}</span>
        </div>
        <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{body}</p>
      </div>
      {!isTenant && (
        <div className={`h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center ${
          isInternal ? 'bg-amber-100' : 'bg-indigo-100'
        }`}>
          <User2 className={`h-4 w-4 ${isInternal ? 'text-amber-700' : 'text-indigo-700'}`} />
        </div>
      )}
    </div>
  );
}

function SidebarCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">{title}</p>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function SidebarRow({ label, children, small }: { label: string; children: React.ReactNode; small?: boolean }) {
  return (
    <div>
      <p className="text-[11px] font-medium text-slate-500 mb-1">{label}</p>
      <div className={small ? 'text-xs text-slate-700' : ''}>{children}</div>
    </div>
  );
}

export default SuperAdminTicketDetailPage;
